import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSlot } from "src/components/Slots/SlotsContext";
import { hasCategories, resolveFilters } from "src/helpers/searchFiltersUrl";
import { useGetConsultants } from "src/hooks/useConsultants";
import { useDepartments } from "src/hooks/useDepartments";
import { useQuestions } from "src/hooks/useQuestions";
import { useSearchFiltersState } from "src/hooks/useSearchFiltersState";
import { useSearchPage } from "src/hooks/useSearchPage";
import { useTerms } from "src/hooks/useTerms";
import newApi from "src/newApi";
import { queryClient } from "src/queryClient";
import { QuestionsListSettingsProvider } from "src/reducers/questions";

import { DefaultSearch } from "./DefaultSearch";
import { ExamFiltersContainer } from "./Filters/ExamFilters/ExamFilters";
import { type ViewType, ViewTypeToggle } from "./Filters/ViewTypeToggle";
import QuestionsView from "./QuestionsView";
import { SearchEmptyState } from "./SearchEmptyState";

export const defaultDetails = {
	departments: [],
	tags: [],
	terms: [],
	consultants: [],
	subtags: [],
	cemVersions: [],
	typeOfExam: null,
	level: [],
	additionalOption: {
		starred: false,
		wrongAnswers: false,
		skipCanceledQuestions: false,
		isNote: false,
		isUnanswered: false,
	},
	hasUnansweredComments: false,
	aiDiscussions: {
		isAiUnapproved: false,
		hasUnresolvedReviews: false,
	},
	searchValue: "",
	questions: [],
};

export type ExamDetails = Record<string, unknown> & typeof defaultDetails;

/** Filtry szukajki pytań ze stanu URL; też lista pytań w adminie (z własnymi kluczami w defaults). */
export function useExamSearchDetails(defaults: ExamDetails = defaultDetails) {
	const [urlDetails, setDetails] = useSearchFiltersState<ExamDetails>(defaults);
	const { data: tree } = useDepartments();
	const { data: terms } = useTerms();
	const { data: consultants } = useGetConsultants();

	// URL ma same id — pełne obiekty (nazwy, index działów) bierzemy z list
	const details = useMemo(
		() =>
			resolveFilters(urlDetails, { tree, terms, consultants }) as ExamDetails,
		[urlDetails, tree, terms, consultants],
	);
	// bez drzewa działów buildQuery źle odfiltrowałby tagi — czekamy
	const isReady = !!tree || !hasCategories(details);

	return { details, setDetails, isReady };
}

export function ExamsSearch() {
	const { t } = useTranslation();
	const [page, setPage] = useSearchPage();
	const [viewType, setViewType] = useState<ViewType>("list");
	const [revealAnswers, setRevealAnswers] = useState(false);
	const SettingsSlot = useSlot("searchQuestionsSettings");
	const { details, setDetails, isReady } = useExamSearchDetails();

	const {
		data: { results, count, counts } = {},
		isLoading,
		isRefetching,
	} = useQuestions(isReady ? details : null, page);

	const clearQuestionsCache = () => {
		queryClient.removeQueries({ queryKey: ["questions"] });
	};

	const handleSetPage = (newPage: number) => {
		clearQuestionsCache();
		setPage(newPage);
	};

	const handleSetDetails = (data: Record<string, unknown>) => {
		clearQuestionsCache();
		setPage(0);
		setDetails(data);
	};

	const changeViewType = (type: ViewType) => {
		setViewType(type);
		handleSetPage(0);
	};

	const aiFilterActive = !!(
		details.aiDiscussions?.isAiUnapproved ||
		details.aiDiscussions?.hasUnresolvedReviews
	);

	return (
		<QuestionsListSettingsProvider aiFilterActive={aiFilterActive}>
			<DefaultSearch
				isLoading={isRefetching}
				renderView={({ scrollToTop, clearFilters }) => (
					<QuestionsView
						viewType={viewType}
						data={results}
						page={page}
						setPage={handleSetPage}
						count={count}
						isLoading={isLoading}
						searchValue={details.searchValue || ""}
						scrollToTop={scrollToTop}
						revealAnswers={revealAnswers}
						emptyState={
							<SearchEmptyState
								type="questions"
								searchValue={details.searchValue || ""}
								onClearFilters={clearFilters}
							/>
						}
					/>
				)}
				details={details}
				setDetails={handleSetDetails}
				filters={(draft, setDraft) => (
					<div className="flex flex-col gap-16">
						<div className="flex items-center justify-between gap-12">
							<span className="size-16-light">
								{t("filters.viewType.select")}
							</span>
							<ViewTypeToggle value={viewType} onChange={changeViewType} />
						</div>
						{SettingsSlot && (
							<SettingsSlot
								revealAnswers={revealAnswers}
								setRevealAnswers={setRevealAnswers}
							/>
						)}
						<ExamFiltersContainer
							examDetails={draft as ExamDetails}
							setExamDetails={setDraft}
						/>
					</div>
				)}
				defaultDetails={defaultDetails}
				countDraft={(draft) =>
					newApi.getExamPreviewDetails(draft).then(({ count }) => count)
				}
				count={count}
				counts={counts}
				activeFilter="questions"
			/>
		</QuestionsListSettingsProvider>
	);
}
